import { useEffect } from 'react';
import { typography, semantic, shadow } from '@/shared/theme/tokens';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDeploymentDetail } from '@/features/deployments/hooks/useDeployments';
import { useDeploymentRole } from '@/app/providers/DeploymentRoleContext';
import { usePermissions } from '@/entities/rbac/hooks/usePermissions';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { UsersTab } from './tabs/UsersTab';
import { ParametersTab } from './tabs/ParametersTab';
import { LayersTab } from './tabs/LayersTab';
import { ShiftsTab } from './tabs/ShiftsTab';
import { NotificationsTab } from './tabs/NotificationsTab';

const VALID_SECTIONS = ['users', 'parameters', 'layers', 'notifications', 'shifts'];

const SECTION_LABELS = {
  users: 'Users',
  parameters: 'Parameters',
  layers: 'Layers',
  notifications: 'Notifications',
  shifts: 'Settings',
};

export default function DeploymentSettingsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: deployment, isLoading } = useDeploymentDetail(id);
  const { canManage, setActiveDeployment } = useDeploymentRole();
  // Use permissions hook directly — avoids context timing race where deploymentId
  // is still null on first render, causing canViewSettings to be false prematurely.
  const { canViewSettings } = usePermissions();
  const c = useThemeColors();
  const isDark = useIsDark();
  const { isMobile } = useBreakpoint();
  const [searchParams] = useSearchParams();
  const rawSection = searchParams.get('section') ?? 'users';
  const activeSection = VALID_SECTIONS.includes(rawSection) ? rawSection : 'users';

  // Register the active deployment in context so all descendants read the right role
  useEffect(() => {
    setActiveDeployment(id);
    return () => setActiveDeployment(null);
  }, [id, setActiveDeployment]);

  // ripik_user and client_user have no business on settings — send them to dashboard
  useEffect(() => {
    if (!isLoading && !canViewSettings(id)) {
      navigate(`/deployments/${id}/dashboard`, { replace: true });
    }
    // Intentional: re-check access only when loading state or the deployment id
    // changes. `canViewSettings` is a selector recreated each render; adding it
    // would re-run the redirect every render. It is invoked here but excluded
    // from the dep array on purpose.
  }, [isLoading, id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: 960 }}>
      <SkeletonCard rows={2} height={80} />
      <SkeletonCard rows={5} height={300} />
    </div>
  );

  if (!deployment) return <NotFound onBack={() => navigate('/deployments')} />;

  return (
    <div style={{ fontFamily: typography.fontFamily.body, width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div style={{ marginBottom: isMobile ? '12px' : '20px' }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? '16px' : '20px', fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading, letterSpacing: typography.letterSpacing.tight }}>
          {SECTION_LABELS[activeSection]}
        </h1>
      </div>

      {/* Section Content */}
      <div style={{ flex: 1, minHeight: 0 }}>
        {activeSection === 'users' && (
          <SettingsIsland label="Users" icon="group" c={c} isDark={isDark} isMobile={isMobile}>
            <UsersTab deploymentId={id} canManage={canManage} />
          </SettingsIsland>
        )}
        {activeSection === 'parameters' && (
          <SettingsIsland label="Parameters" icon="tune" c={c} isDark={isDark} isMobile={isMobile}>
            <ParametersTab deploymentId={id} canManage={canManage} />
          </SettingsIsland>
        )}
        {activeSection === 'layers' && (
          <LayersTab deploymentId={id} canManage={canManage} />
        )}
        {activeSection === 'notifications' && (
          <SettingsIsland label="Notifications" icon="notifications" c={c} isDark={isDark} isMobile={isMobile}>
            <NotificationsTab deploymentId={id} canManage={canManage} />
          </SettingsIsland>
        )}
        {activeSection === 'shifts' && (
          <ShiftsTab deploymentId={id} canManage={canManage} />
        )}
      </div>
    </div>
  );
}

function SettingsIsland({ label, icon, c, isDark, isMobile, children }) {
  return (
    <div style={{
      background: isDark ? 'rgba(255,255,255,0.025)' : '#fff',
      border: `1px solid ${c.border}`,
      borderRadius: isMobile ? 10 : 14,
      boxShadow: shadow.panel,
      overflow: 'hidden',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Island header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: isMobile ? '10px 14px' : '14px 20px',
        borderBottom: `1px solid ${c.border}`,
        background: isDark ? 'rgba(255,255,255,0.02)' : c.bgSubtle,
        flexShrink: 0,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 17, color: c.textSubtle }}>{icon}</span>
        <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.body, letterSpacing: typography.letterSpacing.normal }}>
          {label}
        </span>
      </div>
      {/* Island body */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: isMobile ? '12px' : '20px' }}>
        {children}
      </div>
    </div>
  );
}

function NotFound({ onBack }) {
  const { textMuted } = useThemeColors();
  return (
    <div style={{ padding: '60px', textAlign: 'center' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 40, color: semantic.error, display: 'block', marginBottom: '12px' }}>error</span>
      <p style={{ fontSize: typography.fontSize.lg, color: textMuted }}>Deployment not found.</p>
      <PrimaryButton variant="secondary" icon="arrow_back" onClick={onBack}>Back</PrimaryButton>
    </div>
  );
}
